import React from "react"
import Select, { components } from 'react-select';
import { asCurrency, debounceMs, formatDate, humanizeLabel } from "../../services/Utils"
import { debounce } from "lodash-es"
import { CatalogFetcher } from "../../services/CatalogFetcher"
import { describeReturnableContainers, returnableContainersDeposit } from "../../services/ReturnableContainers"

const Option = (innerProps) => {
  const option = innerProps.data
  let unlistedBadge
  if ( option.temporarily_disabled ) {
    unlistedBadge = (
      <div>
        <span className='badge badge-pill badge-secondary'>
          <span className="fa fa-pause-circle mr-1"></span>
          Temporarily Disabled
        </span>
      </div>
    )
  } else if ( !option.published ) {
    unlistedBadge = (
      <div>
        <span className='badge badge-pill badge-danger'>
          <span className="fa fa-toggle-off mr-1"></span>
          Unlisted
        </span>
      </div>
    )
  }
  let availableIcon = ''
  if ( option.published && option.unavailable_until_description ) {
   availableIcon = (
    <span className='badge badge-pill badge-warning'>
      <span className="fa fa-clock mr-1"></span>
      { option.unavailable_until_description }
    </span>
   )
  }

  const mutedUnavailable = option.published ? "select-option" : "select-option text-muted"
  let minimum, maximum
  if (option.quantity_minimum > 1) {
    minimum = <span className="badge border ml-2">{`${option.quantity_minimum} minimum`}</span>
  }
  if (option.quantity_maximum) {
    maximum = <span className="badge border ml-2">{`${option.quantity_maximum} maximum`}</span>
  }
  let sku
  if (option.sku) {
    sku = <span className="badge border ml-2 text-muted text-monospace">Item# { option.sku }</span>
  }
  let city_state_tag
  if (option.city && option.state) {
    city_state_tag = (

      <small>
        <span className='mx-2'>•</span>
        {option.city}, {option.state}
      </small>
    )
  }

  let originalPrice = '';
  if ( option.original_price ) {
    originalPrice = <span className='text-muted small text-strike mr-1 tabular-nums'>{ asCurrency(option.original_price)}</span>
  }
  let discountIndicator = '';
  if ( option.discount_indicator ) {
    discountIndicator = <div><span className='badge badge-success badge-pill'>{ option.discount_indicator }</span></div>
  }

  const depositState = innerProps.selectProps.depositState
  const returnableContainers = option.returnable_containers || []
  const unitDeposit = Number(option.deposit || 0) + returnableContainersDeposit(returnableContainers)
  const deposits = [unitDeposit, depositState && option.state_deposits[depositState]]
    .map((amount) => Number(amount || 0))
    .filter((amount) => amount != 0)
  const depositLines = deposits.map((amount, index) => (
    <div key={ index } className='small'>
      <span className='fa fa-recycle mr-1'></span>
      <span className='tabular-nums'>{ asCurrency(amount) }</span>
    </div>
  ))
  let returnableContainersDescription
  if (returnableContainers.length > 0) {
    returnableContainersDescription = (
      <div className='small text-muted'>{ describeReturnableContainers(returnableContainers) }</div>
    )
  }

  // A $0 item with a deposit shows only its deposits, not the $0 price.
  let price
  if ( Number(option.final_price) != 0 || deposits.length == 0 ) {
    price = <span className='tabular-nums'>{ asCurrency(option.final_price) }</span>
  }
  const children = (
    <div className={mutedUnavailable}>
      <div className='d-flex'>
        <div className='mr-2'>
          <img src={option.product_photo.small_url} height="60" />
        </div>
        <div className='w-100'>
          <div className="text-muted">
            <small>{option.producer_name}</small>
            { city_state_tag }
          </div>
          <div className='d-flex justify-content-between'>
            <div>
              <div className="font-weight-bold">{ option.name }</div>
              <div>{ option.unit }{ sku }{ minimum }{ maximum }</div>
              { returnableContainersDescription }
              { unlistedBadge }
            </div>
            <div className="text-right">
              <div>{ availableIcon }</div>
              { originalPrice }
              { price }
              { depositLines }
              { discountIndicator }
              { option.is_short_sale && (
                <div>
                  <span className="badge badge-dark mt-1">
                    Short Sale: { humanizeLabel(option.date_label_type) } { formatDate(option.short_sale_date_label_on) }
                  </span>
                </div>
              ) }
            </div>
          </div>
        </div>
      </div>
    </div>
  )
  return <components.Option {...innerProps} children={ children } />
}

const Input = (innerProps) => {
  return <components.Input {...innerProps} name="product-unit" />
}

const SingleValue = (innerProps) => {
  const option = innerProps.data
  let minimum

  if (!option) {
    return (<components.SingleValue {...innerProps} children={ <span>Nothing</span> }/>)
  }

  if (option.quantity_minimum > 1) {
    minimum = <span className="badge border ml-2">{`${option.quantity_minimum} minimum`}</span>
  }
  const children = (
    <span>
      <span className='small font-weight-muted mr-2'>{ option.producer_name }</span>
      <span className="font-weight-bold mr-2">{ option.name }</span>
      <span className="mr-4">({ option.unit })</span>
      { minimum }
    </span>
  )
  return <components.SingleValue {...innerProps} children={ children }/>
}


export class ProductUnitSelect extends React.Component {
  constructor(props) {
    super(props)

    this.searchInput = React.createRef()
    this.refocus = this.refocus.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onInputChange = this.onInputChange.bind(this)
    this.search = debounce(this.search, debounceMs(500))

    this.state = { inputValue: "", options: [] }
  }

  onChange (product_unit) {
    this.props.onProductUnitSelected(product_unit)
  }

  search() {
    if (!this.state.inputValue) { this.setState({ options: [] }); return }
    let params = this.props.filters || {}

    params.text = this.state.inputValue
    params.categories = []
    params.producers = []

    if ( this.props.buyer_id )
      params.buyerId = this.props.buyer_id

    this.setState({ isLoading: true })

    CatalogFetcher.queryAvailable(this.props.seller_id, 1, 25, params)
      .then((response) => { this.setState({ options: response.items, isLoading: false }) })
  }


  onInputChange(input) {
    this.setState({ inputValue: input }, this.search)
  }

  filterOption(option) {
    // Do no filtering, just return all options
    return true
  }

  noOptionsMessage(option) {
    return option.inputValue ? "No options" : "Type to see product units..."
  }

  refocus() {
    this.searchInput.current.focus()
  }

  render () {
    return (
      <Select
        ref={ this.searchInput }
        value={ this.props.value }
        components={{ Option: Option, SingleValue: SingleValue, Input: Input }}
        placeholder="Search for products..."
        noOptionsMessage={ this.noOptionsMessage }
        onChange={ this.onChange }
        inputValue={ this.state.inputValue }
        isOptionSelected={ () => {false} }
        isOptionDisabled={ (option) => option.temporarily_disabled }
        onInputChange={ this.onInputChange }
        filterOption={ this.filterOption }
        maxMenuHeightnumber={400}
        isLoading={ this.state.isLoading }
        isClearable={ true }
        options={ this.state.options }
        depositState={ this.props.depositState }
      />
    );
  }
}
export default ProductUnitSelect